import React from 'react';
import {
  UserGroupIcon,
  PlusIcon,
  EyeIcon,
  PencilIcon,
  TrashIcon,
  CameraIcon,
  KeyIcon,
  MapPinIcon,
  DocumentTextIcon,
  CheckCircleIcon,
  SparklesIcon,
  ComputerDesktopIcon,
  DevicePhoneMobileIcon,
  ArrowRightIcon,
  XMarkIcon,
  LightBulbIcon
} from '@heroicons/react/24/outline';

export default function UserManagementHelp() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full mb-6">
            <UserGroupIcon className="w-8 h-8 text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">User Management System</h1>
          <p className="text-lg md:text-xl text-gray-600 max-w-2xl mx-auto">
            Complete guide to managing your team members, from hiring to ongoing administration
          </p>
        </div>

        {/* Overview Card */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <div className="flex items-start space-x-2 md:space-x-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center">
                <LightBulbIcon className="w-6 h-6 text-indigo-600" />
              </div>
            </div>
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">Managing Your Team Made Simple</h2>
              <p className="text-gray-600 text-base md:text-lg leading-relaxed">
                Your user management system is the heart of your business operations. Here you'll add new team members, update their information, manage access levels, and keep everything organized. Think of it as your digital HR department that grows with your company.
              </p>
            </div>
          </div>
        </div>

        {/* System Overview Diagram */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6 md:mb-8 text-center">How the System Works Together</h2>

          <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
              {/* Users Manager */}
              <div className="relative flex flex-col items-center">
                <div className="bg-indigo-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-indigo-600 font-bold text-xl">1</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Users Manager</h3>
                  <p className="text-sm text-gray-600">Main dashboard showing all your team members</p>
                  <div className="mt-3 p-3 bg-indigo-50 rounded-lg border border-indigo-200">
                    <UserGroupIcon className="w-6 h-6 text-indigo-600 mx-auto mb-1" />
                    <p className="text-xs text-indigo-700 font-medium">Team Overview</p>
                  </div>
                </div>
                {/* Arrow for desktop */}
                <div className="hidden md:flex items-center justify-center mt-4">
                  <ArrowRightIcon className="w-6 h-6 text-indigo-400" />
                </div>
              </div>

              {/* New User */}
              <div className="relative flex flex-col items-center">
                <div className="bg-purple-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-purple-600 font-bold text-xl">2</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Add New User</h3>
                  <p className="text-sm text-gray-600">Create new team member profiles with all details</p>
                  <div className="mt-3 p-3 bg-purple-50 rounded-lg border border-purple-200">
                    <PlusIcon className="w-6 h-6 text-purple-600 mx-auto mb-1" />
                    <p className="text-xs text-purple-700 font-medium">New Hire</p>
                  </div>
                </div>
                {/* Arrow for desktop */}
                <div className="hidden md:flex items-center justify-center mt-4">
                  <ArrowRightIcon className="w-6 h-6 text-purple-400" />
                </div>
              </div>

              {/* More Info */}
              <div className="relative flex flex-col items-center">
                <div className="bg-pink-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
                  <span className="text-pink-600 font-bold text-xl">3</span>
                </div>
                <div className="text-center">
                  <h3 className="font-semibold text-gray-900 mb-2">Detailed View</h3>
                  <p className="text-sm text-gray-600">Edit and manage individual user information</p>
                  <div className="mt-3 p-3 bg-pink-50 rounded-lg border border-pink-200">
                    <EyeIcon className="w-6 h-6 text-pink-600 mx-auto mb-1" />
                    <p className="text-xs text-pink-700 font-medium">User Details</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Mobile flow indicator */}
            <div className="md:hidden mt-6 flex justify-center">
              <div className="flex items-center space-x-2 text-sm text-gray-500">
                <span>Flow:</span>
                <span className="bg-indigo-100 text-indigo-700 px-2 py-1 rounded">1</span>
                <ArrowRightIcon className="w-4 h-4" />
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">2</span>
                <ArrowRightIcon className="w-4 h-4" />
                <span className="bg-pink-100 text-pink-700 px-2 py-1 rounded">3</span>
              </div>
            </div>
          </div>
        </div>

        {/* Component Features */}
        <div className="grid md:grid-cols-3 gap-4 md:gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
              <UserGroupIcon className="w-6 h-6 text-indigo-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">Users Manager</h3>
            <p className="text-gray-600 text-sm md:text-base mb-3">Your main team dashboard showing all current staff members with their photos, roles, and access levels.</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• View all team members at a glance</li>
              <li>• See user access levels and verification status</li>
              <li>• Quick actions for each user</li>
              <li>• Company usage statistics</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center mb-4">
              <PlusIcon className="w-6 h-6 text-purple-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">New User Form</h3>
            <p className="text-gray-600 text-sm md:text-base mb-3">Comprehensive form to add new team members with all necessary information for HR and payroll.</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Personal and contact details</li>
              <li>• Job role and department assignment</li>
              <li>• Salary and contract information</li>
              <li>• Emergency contact details</li>
            </ul>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-12 h-12 bg-pink-100 rounded-lg flex items-center justify-center mb-4">
              <EyeIcon className="w-6 h-6 text-pink-600" />
            </div>
            <h3 className="text-base md:text-lg font-semibold text-gray-900 mb-2">More Info View</h3>
            <p className="text-gray-600 text-sm md:text-base mb-3">Detailed user profile page for viewing and editing individual team member information.</p>
            <ul className="text-sm text-gray-600 space-y-1">
              <li>• Edit personal and job details</li>
              <li>• Change access permissions</li>
              <li>• Update contract information</li>
              <li>• View employment history</li>
            </ul>
          </div>
        </div>

        {/* Step-by-Step Workflow */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 mb-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <SparklesIcon className="w-6 h-6 mr-3 text-indigo-500" />
            Complete User Management Workflow
          </h2>
          <div className="space-y-4">
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">1</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Start in Users Manager</h3>
                <p className="text-gray-600">Open the Users Manager to see your current team and check available user slots</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">2</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Add New Team Member</h3>
                <p className="text-gray-600">Click "Create New User" to open the comprehensive new user form</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">3</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Fill Out Details</h3>
                <p className="text-gray-600">Complete all required information including personal details, job role, salary, and emergency contacts</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">4</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Review and Save</h3>
                <p className="text-gray-600">Review all information and submit to create the new user account</p>
              </div>
            </div>
            <div className="flex items-start space-x-2 md:space-x-4">
              <div className="flex-shrink-0 w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center">
                <span className="text-indigo-600 font-bold text-sm">5</span>
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">Manage Ongoing</h3>
                <p className="text-gray-600">Use More Info view to update details, change access levels, or manage user accounts</p>
              </div>
            </div>
          </div>
        </div>

        {/* Key Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center mb-3">
              <CameraIcon className="w-5 h-5 text-green-600" />
            </div>
            <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-2">Profile Pictures</h3>
            <p className="text-gray-600 text-xs md:text-sm">Upload photos or take pictures directly in the app</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mb-3">
              <KeyIcon className="w-5 h-5 text-blue-600" />
            </div>
            <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-2">Password Reset</h3>
            <p className="text-gray-600 text-xs md:text-sm">Securely reset passwords for any team member</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 bg-yellow-100 rounded-lg flex items-center justify-center mb-3">
              <MapPinIcon className="w-5 h-5 text-yellow-600" />
            </div>
            <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-2">Location Tracking</h3>
            <p className="text-gray-600 text-xs md:text-sm">View company location and manage user addresses</p>
          </div>

          <div className="bg-white rounded-xl shadow-md p-4 md:p-6 border border-gray-100 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center mb-3">
              <DocumentTextIcon className="w-5 h-5 text-red-600" />
            </div>
            <h3 className="text-sm md:text-base font-semibold text-gray-900 mb-2">Data Export</h3>
            <p className="text-gray-600 text-xs md:text-sm">Download user data as CSV before account deletion</p>
          </div>
        </div>

        {/* Benefits Section */}
        <div className="bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl p-6 md:p-8 text-white mb-8">
          <h2 className="text-xl md:text-2xl font-bold mb-4 md:mb-6">Why User Management Matters</h2>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Complete Team Overview</h3>
                <p className="text-indigo-100">See all your staff in one place with their roles, access levels, and status</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Streamlined Onboarding</h3>
                <p className="text-indigo-100">Add new team members quickly with comprehensive forms that capture everything needed</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Access Control</h3>
                <p className="text-indigo-100">Manage who can do what with granular permission levels</p>
              </div>
            </div>
            <div className="flex items-start space-x-3">
              <CheckCircleIcon className="w-6 h-6 text-green-300 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold mb-1">Data Security</h3>
                <p className="text-indigo-100">Secure user data with proper export options and controlled deletions</p>
              </div>
            </div>
          </div>
        </div>

        {/* Tips & Best Practices */}
        <div className="bg-white rounded-2xl shadow-lg p-6 md:p-8 border border-gray-100">
          <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-4 md:mb-6 flex items-center">
            <LightBulbIcon className="w-6 h-6 mr-3 text-indigo-500" />
            Best Practices & Tips
          </h2>
          <div className="grid md:grid-cols-2 gap-4 md:gap-6">
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-indigo-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Always verify email addresses before creating accounts</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-indigo-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Set appropriate access levels based on job responsibilities</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-indigo-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Keep emergency contact information up to date</p>
              </div>
            </div>
            <div className="space-y-3">
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-indigo-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Regularly review and update user permissions</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-indigo-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Export user data before making major account changes</p>
              </div>
              <div className="flex items-start space-x-3">
                <div className="w-6 h-6 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-indigo-600 font-bold text-xs">•</span>
                </div>
                <p className="text-gray-600 text-sm">Use profile pictures to personalize the team experience</p>
              </div>
            </div>
          </div>

          <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
            <div className="flex items-start space-x-3">
              <DocumentTextIcon className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="font-semibold text-blue-800">Data Protection</h3>
                <p className="text-blue-700 text-sm">
                  Remember that user data is sensitive. Always follow data protection regulations when managing personal information, and consider consulting legal experts for compliance questions.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}